import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  CardDocuments,
  CardOrderFinance,
  CardOrderPaymentProgress,
  EmptyState,
  TransactionDrawerForm,
} from '@/components'
import { AddCircleOutlineIcon, Button, Text } from '@mercai/clever'
import { TransactionInfo } from '../transaction-info'
import { OrderFinanceDrawerForm } from '../order-finance-drawer-form'

import { QUERIES_ADMIN, useTransactionDelete } from '@/services'
import { onConfirmDangerMessage, onSuccessMessage } from '@/helpers'

import { CardDocumentsDataProps } from '@/components/card-documents/card-documents.type'
import { FinanceFormTabsEnum } from '@/components/forms/finance-form/finance-form.type'
import { TabClientDepositProps } from './tab-client-deposit.type'
import { ProfileProps, SaleOrderStatusEnum } from '@/types'
import styles from './tab-client-deposit.module.css'

export const TabClientDeposit = ({
  isLoading,
  saleOrder,
}: TabClientDepositProps) => {
  const { t } = useTranslation('screenOrderCenterPage')

  const queryClient = useQueryClient()

  const noEditableStatuses = [
    SaleOrderStatusEnum.completed,
    SaleOrderStatusEnum.cancelled,
  ]

  const isEditable = !noEditableStatuses.includes(
    saleOrder?.status as SaleOrderStatusEnum,
  )

  const [isOpenFinanceDrawerForm, setIsOpenFinanceDrawerForm] = useState<
    keyof typeof FinanceFormTabsEnum | ''
  >('')

  const [isOpenTransactionDrawerForm, setIsOpenTransactionDrawerForm] =
    useState(false)

  const [transactionUuid, setTransactionUuid] = useState('')

  const { mutateAsync: onDeleteTransaction } = useTransactionDelete()

  const onHandleUpdateTransaction = (uuid: string) => {
    setTransactionUuid(uuid)
    setIsOpenTransactionDrawerForm(true)
  }

  const onHandleNewTransaction = () => {
    setTransactionUuid('')
    setIsOpenTransactionDrawerForm(true)
  }

  const onHandleDeleteTransaction = async (uuid: string) => {
    const confirm = await onConfirmDangerMessage(
      t('orderCenterShowTabClientDeleteTransactionConfirmTitle'),
      t('orderCenterShowTabClientDeleteTransactionConfirmDescription'),
      t('orderCenterShowTabClientDeleteTransactionConfirmButton'),
      t('orderCenterShowTabClientDeleteTransactionCancelButton'),
    )

    if (!confirm) return

    const response = await onDeleteTransaction(uuid)

    if (response.success) {
      onSuccessMessage(
        t('orderCenterShowTabClientDeleteTransactionSuccessTitle'),
        t('orderCenterShowTabClientDeleteTransactionSuccessDescription'),
      )

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.ORDER_CENTER.module],
      })
    } else {
      onSuccessMessage(
        t('orderCenterShowTabClientDeleteTransactionErrorTitle'),
        t('orderCenterShowTabClientDeleteTransactionErrorDescription'),
      )
    }
  }

  const documents: CardDocumentsDataProps[] = useMemo(() => {
    const docs: { file: string; title: string }[] = []

    saleOrder?.invoices?.forEach((invoice, index) => {
      if (invoice?.pdfFile) {
        docs.push({
          file: invoice.pdfFile || '',
          title:
            t('orderCenterShowTabClientDepositInvoiceTitle') +
            ` ${saleOrder?.invoices.length > 1 ? index + 1 : ''}`,
        })
      }

      if (invoice?.xmlFile) {
        docs.push({
          file: invoice.xmlFile || '',
          title:
            t('orderCenterShowTabClientDepositInvoiceTitle') +
            ` ${saleOrder?.invoices.length > 1 ? index + 1 : ''}`,
        })
      }

      if (invoice?.deductionPdfFile) {
        docs.push({
          file: invoice.deductionPdfFile || '',
          title: t('orderCenterShowTabClientDeductionTitlePdf'),
        })
      }

      if (invoice?.deductionXmlFile) {
        docs.push({
          file: invoice.deductionPdfFile || '',
          title: t('orderCenterShowTabClientDeductionTitleXml'),
        })
      }
    })

    saleOrder?.payments?.forEach((payment, index) => {
      if (payment?.proof) {
        docs.push({
          file: payment.proof || '',
          title:
            t('orderCenterShowTabClientProofTitle') +
            ` ${saleOrder?.payments.length > 1 ? index + 1 : ''}`,
        })
      }
    })

    return docs
  }, [saleOrder])

  return (
    <>
      <div className={styles.container}>
        <div className={styles.transactions}>
          <div className={styles['transactions-header']}>
            <Text variant="title-medium">
              {t('orderCenterShowTabClientDepositsTitle')}
            </Text>

            {isEditable && (
              <Button variant="success-light" onClick={onHandleNewTransaction}>
                {t('orderCenterShowTabClientDepositsAddButton')}
                <AddCircleOutlineIcon />
              </Button>
            )}
          </div>

          {isLoading && (
            <div className={styles['transactions-content']}>
              <TransactionInfo isLoading />
            </div>
          )}

          {!isLoading && saleOrder && saleOrder?.transactions?.length > 0 && (
            <div className={styles['transactions-content']}>
              {saleOrder?.transactions?.map((transaction, index) => {
                return (
                  <TransactionInfo
                    key={index}
                    transaction={transaction}
                    orderReference={saleOrder?.reference}
                    isLoading={isLoading}
                    onUpdate={() => onHandleUpdateTransaction(transaction.uuid)}
                    {...(transaction.paymentSource === 'internal' && {
                      onDelete: () =>
                        onHandleDeleteTransaction(transaction.uuid),
                    })}
                  />
                )
              })}
            </div>
          )}

          {!isLoading &&
            (!saleOrder || saleOrder?.transactions?.length === 0) && (
              <EmptyState
                horizontal
                title={t('orderCenterShowTabClientEmptyTitle')}
                description={t('orderCenterShowTabClientEmptyDescription')}
              />
            )}
        </div>

        <div className={styles.sidebar}>
          <CardDocuments
            isLoading={isLoading}
            data={documents}
            onAdd={() => setIsOpenFinanceDrawerForm('invoices')}
            isEditable={isEditable}
          />

          <CardOrderFinance
            invoiceStatus={saleOrder?.invoices?.[0]?.status}
            calculatePaymentStatus={saleOrder?.paymentsStatus}
            paymentMethod={saleOrder?.buyer?.paymentMethod}
            paymentForm={saleOrder?.buyer?.paymentForm}
            useCfdi={saleOrder?.buyer?.cfdi}
            financingCost={saleOrder?.financingCost}
            isLoading={!!isLoading}
            onEdit={() => setIsOpenFinanceDrawerForm('payments')}
            saleOrderStatus={saleOrder?.status}
          />

          <CardOrderPaymentProgress
            isLoading={!!isLoading}
            saleOrder={saleOrder}
          />
        </div>
      </div>

      {saleOrder?.uuid && (
        <>
          <TransactionDrawerForm
            uuid={transactionUuid}
            orderUuid={saleOrder.uuid}
            payer={saleOrder?.buyer as unknown as ProfileProps}
            isOpen={isOpenTransactionDrawerForm}
            onHandleOpen={() => setIsOpenTransactionDrawerForm(false)}
            hasOrderStep={false}
          />
          <OrderFinanceDrawerForm
            initialValues={saleOrder}
            initialTab={isOpenFinanceDrawerForm || 'invoices'}
            isOpen={!!isOpenFinanceDrawerForm}
            onHandleClose={() => setIsOpenFinanceDrawerForm('')}
          />
        </>
      )}
    </>
  )
}
